import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, UserX, RefreshCw, Sparkles } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface PendingRdv {
  id: string;
  prospect_name: string;
  company_name: string;
  job_title: string | null;
  date: string;
  time: string | null;
}

type Answer = 'showed' | 'no_show' | 'rescheduled';

interface ClientRdvConfirmationModalProps {
  clientId: string;
}

const ClientRdvConfirmationModal: React.FC<ClientRdvConfirmationModalProps> = ({ clientId }) => {
  const [rdvs, setRdvs] = useState<PendingRdv[]>([]);
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [visible, setVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem('rdv_confirm_shown')) return;

    const fetchPending = async () => {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().split('T')[0];

      const { data, error } = await supabase
        .from('appointments')
        .select('id, prospect_name, company_name, job_title, date, time')
        .eq('client_id', clientId)
        .lte('date', yesterdayStr)
        .in('status', ['planifié', 'confirmé', 'Confirmed', 'Scheduled', 'scheduled', 'confirmed'])
        .order('date', { ascending: false })
        .limit(10);

      if (error || !data || data.length === 0) return;

      setRdvs(data);
      setVisible(true);
      sessionStorage.setItem('rdv_confirm_shown', '1');
    };

    fetchPending();
  }, [clientId]);

  const handleAnswer = (id: string, answer: Answer) => {
    setAnswers(prev => ({ ...prev, [id]: answer }));
  };

  const allAnswered = rdvs.length > 0 && Object.keys(answers).length === rdvs.length;
  const answeredCount = Object.keys(answers).length;

  const handleConfirm = async () => {
    setSaving(true);
    try {
      const updates = Object.entries(answers).map(([id, status]) =>
        supabase.from('appointments').update({ status }).eq('id', id)
      );
      await Promise.all(updates);
      setDone(true);
      setTimeout(() => setVisible(false), 1500);
    } catch (err) {
      console.error('Error updating appointments:', err);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  if (!visible) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        >
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setVisible(false)}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
          >
            {done ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center justify-center py-16 px-6"
              >
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                  <Check className="h-8 w-8 text-emerald-600" />
                </div>
                <p className="text-lg font-semibold text-gray-900">Confirmations enregistrees</p>
                <p className="text-sm text-gray-500 mt-1">Merci pour votre retour !</p>
              </motion.div>
            ) : (
              <>
                <div className="px-6 py-5 bg-[#0a0a0a] text-white relative">
                  <button
                    onClick={() => setVisible(false)}
                    className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                  >
                    <X className="h-5 w-5 text-white/70" />
                  </button>

                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-[#E91E8C] to-[#ff5db8] text-xs font-medium mb-3">
                    <Sparkles className="h-3 w-3" />
                    Suivi de vos RDVs
                  </div>
                  <h2 className="text-xl font-bold">Ces rendez-vous ont-ils eu lieu ?</h2>
                  <p className="text-sm text-white/60 mt-1">
                    {rdvs.length} RDV{rdvs.length > 1 ? 's' : ''} en attente de confirmation
                  </p>
                </div>

                <div className="px-6 py-4 max-h-[60vh] overflow-y-auto space-y-3">
                  {rdvs.map((rdv, index) => {
                    const answer = answers[rdv.id];
                    const cardBg = answer === 'showed'
                      ? 'bg-emerald-50 border-emerald-200'
                      : answer === 'no_show'
                      ? 'bg-red-50 border-red-200'
                      : answer === 'rescheduled'
                      ? 'bg-amber-50 border-amber-200'
                      : 'bg-white border-gray-200';

                    return (
                      <motion.div
                        key={rdv.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className={`rounded-xl border p-4 transition-colors ${cardBg}`}
                      >
                        <div className="flex items-start gap-3 mb-3">
                          <div className="flex-shrink-0 text-center bg-gray-100 rounded-lg px-2.5 py-1.5 min-w-[60px]">
                            <div className="text-xs font-semibold text-gray-900">
                              {formatDate(rdv.date)}
                            </div>
                            {rdv.time && (
                              <div className="text-[10px] text-gray-500 mt-0.5">
                                {rdv.time.slice(0, 5)}
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">
                              {rdv.prospect_name || 'Prospect'}
                            </p>
                            {rdv.company_name && (
                              <p className="text-xs text-gray-600 truncate">{rdv.company_name}</p>
                            )}
                            {rdv.job_title && (
                              <p className="text-xs text-gray-400 truncate">{rdv.job_title}</p>
                            )}
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <button
                            onClick={() => handleAnswer(rdv.id, 'showed')}
                            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                              answer === 'showed'
                                ? 'bg-emerald-500 text-white shadow-sm'
                                : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200'
                            }`}
                          >
                            <Check className="h-3.5 w-3.5" />
                            Effectue
                          </button>
                          <button
                            onClick={() => handleAnswer(rdv.id, 'no_show')}
                            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                              answer === 'no_show'
                                ? 'bg-red-500 text-white shadow-sm'
                                : 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'
                            }`}
                          >
                            <UserX className="h-3.5 w-3.5" />
                            No-show
                          </button>
                          <button
                            onClick={() => handleAnswer(rdv.id, 'rescheduled')}
                            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                              answer === 'rescheduled'
                                ? 'bg-amber-500 text-white shadow-sm'
                                : 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'
                            }`}
                          >
                            <RefreshCw className="h-3.5 w-3.5" />
                            Reporte
                          </button>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>

                <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-[#f5f5f8]">
                  <button
                    onClick={() => setVisible(false)}
                    className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    Me le rappeler plus tard
                  </button>
                  <button
                    onClick={handleConfirm}
                    disabled={!allAnswered || saving}
                    className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                      allAnswered && !saving
                        ? 'bg-gradient-to-r from-[#E91E8C] to-[#ff5db8] text-white shadow-lg shadow-pink-500/20 hover:shadow-pink-500/40'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {saving ? 'Enregistrement...' : `Confirmer (${answeredCount}/${rdvs.length})`}
                  </button>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ClientRdvConfirmationModal;
